import { Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import Button, { ButtonThemes } from 'ui-components/button/button';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import XGSFormTextarea from 'ui-components/form/textarea/xgsFormTextarea';
import XGSFormDate from 'ui-components/form/date/xgsFormDate';
import { currentOrClosestBusinessDay } from 'hooks/utils';
import { useCreateDrop } from '../hooks/useDrop';
import { useSelector } from 'react-redux';
import { staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import { removeEmptyValues } from '../utils/utils';
import { CreateDropPayload } from 'app/data/static-load/model';

interface AddDropFormValues {
  terminalNumber: string;
  dropNumber: string;
  destinationDivision: string;
  earlyArrivalDate: string;
  earlyArrivalTime: string;
  earlyDepartureDate: string;
  earlyDepartureTime: string;
  miles: string;
  tractorNumber: string;
  notes: string;
}

interface AddDropSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  refetchLaneByPlanID: () => void;
}

const AddDropSidebar: React.FC<AddDropSidebarProps> = ({ isOpen,
  onClose, refetchLaneByPlanID }) => {

  const [earlyArrivalDate, setEarlyArrivalDate] = useState<string | undefined>();
  const [earlyDepartureDate, setEarlyDepartureDate] = useState<string | undefined>();

  const createDropMutation = useCreateDrop();  

  const staticLoadSelectorData = useSelector(staticLoadSelector);
  const selectedPlanId = staticLoadSelectorData.selectedPlanId;

  return <SlideOutSidebar
    show={isOpen}
    onClose={onClose}
    header="Add Drop"
    size="small"
    className="filter-sidebar"
  >
    <Formik
      initialValues={{
        terminalNumber: '',
        dropNumber: '',
        destinationDivision: '',
        earlyArrivalDate: '',
        earlyArrivalTime: '',
        earlyDepartureDate: '',
        earlyDepartureTime: '',
        miles: '',
        tractorNumber: '',
        notes: '',
      }} onSubmit={(values) => {
        const payload = removeEmptyValues({
          terminalNumber: values.terminalNumber,
          dropNumber: values.dropNumber,
          destinationDivision: values.destinationDivision,
          earlyArrivalDate: values.earlyArrivalDate,
          earlyArrivalTime: values.earlyArrivalTime,
          earlyDepartureDate: values.earlyDepartureDate,
          earlyDepartureTime: values.earlyDepartureTime,
          miles: values.miles,
          tractorNumber: values.tractorNumber,
          notes: values.notes,
        })
        createDropMutation.mutate({
          ...payload,
          planId: selectedPlanId || '',
        } as CreateDropPayload, {
          onSuccess: () => {
            onClose();
            refetchLaneByPlanID()
          }
        })
      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<AddDropFormValues>) => (
        <Form>
          <div className="update-lane-container">
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="dropNumber"
                labelMode={LabelModes.column}
                label="Drop Number"
                className="add-edit-address-section__width-50"
              />
              <XGSFormInput
                type="text"
                name="tractorNumber"
                labelMode={LabelModes.column}
                label="Tractor Number"
                className="add-edit-address-section__width-50"
              />

            </div>
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="terminalNumber"
                labelMode={LabelModes.column}
                label="Terminal Number"
                className="add-edit-address-section__width-50"
              />
              <XGSFormInput
                type="text"
                name="destinationDivision"
                labelMode={LabelModes.column}
                label="Destination Division"
                className="add-edit-address-section__width-50"
              />
            </div>
            <div>
              <XGSFormInput
                type="text"
                name="miles"
                labelMode={LabelModes.column}
                label="Miles"
                className="add-edit-address-section__width-50"
              />
            </div>
            <div className="update-lane-grid-item_date">
              <XGSFormDate
                name="earlyArrivalDate"
                label="Early Arrival Date "
                placeholder="Select date..."
                onDateChange={(date) => { setEarlyArrivalDate(date) }}
                value={earlyArrivalDate}
                onChange={() => null}
                minDate={new Date(currentOrClosestBusinessDay())}
                labelMode={LabelModes.column}
                filterDate={(date) => date.getDay() === 1}
                disableWeekends={true}
              />
              <div className="update-lane-grid-item_time" >
                <label style={{ fontSize: "14px", lineHeight: "24px" }}>Early Arrival Time</label>
                <XGSFormInput
                  name="earlyArrivalTime"
                  type="time"
                  className="time-picker-label"
                  placeholder=""
                  value={formikProps.values.earlyArrivalTime || ""}
                  onChange={(e) => {
                    formikProps.setFieldValue("earlyArrivalTime", e.currentTarget.value);
                  }}
                />
              </div>
            </div>
            <div className="update-lane-grid-item_date">
              <XGSFormDate
                name="earlyDepartureDate"
                label="Early Departure Date "
                placeholder="Select date..."
                onDateChange={(date) => { setEarlyDepartureDate(date) }}
                value={earlyDepartureDate}
                onChange={() => null}
                minDate={new Date(currentOrClosestBusinessDay())}

                labelMode={LabelModes.column}
                filterDate={(date) => date.getDay() === 1}
                disableWeekends={true}
              />
              <div className="update-lane-grid-item_time" >
                <label style={{ fontSize: "14px", lineHeight: "24px" }}>Early Departure Time</label>
                <XGSFormInput
                  name="earlyDepartureTime"
                  type="time"
                  className="time-picker-label"
                  placeholder=""
                  value={formikProps.values.earlyDepartureTime || ""}
                  onChange={(e) => {
                    formikProps.setFieldValue("earlyDepartureTime", e.currentTarget.value);
                  }}
                />
              </div>
            </div>


            <XGSFormTextarea
              name="notes"
              label="Notes"
              required={false}
            />
            <div style={{ marginTop: "10px" }} />
            <div className="copy-move-lane-actions">
              <Button type="button" theme={ButtonThemes.gray} onClick={onClose}>Cancel</Button>
              <Button type="submit" theme={ButtonThemes.blue} spinner={createDropMutation.isLoading}>Confirm</Button>
            </div>

          </div>
        </Form>
      )}
    </Formik>
  </SlideOutSidebar>;
};

export default AddDropSidebar;
